import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';

const HOST_API = process.env.REACT_APP_URL;

const formatMoney = (n) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n ?? 0);

const formatPeriodo = (p) => {
    if (!p) return '—';
    const label = new Date(p).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const estadoBadge = (row) => {
    const alDia = (row.saldoFinal ?? 0) <= 0;
    const s = alDia
        ? { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5', label: 'Al día' }
        : { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'Pendiente' };
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
        }}>
            {s.label}
        </span>
    );
};

const EstadoCuentaAdmin = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const [ufs, setUfs] = useState([]);
    const [ufSel, setUfSel] = useState(null);
    const [periodo, setPeriodo] = useState(new Date());

    const [estadoCuenta, setEstadoCuenta] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buscado, setBuscado] = useState(false);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data ?? []))
            .catch(() => {});
    }, [barrio?.id, administracionId]);

    const buscarEstadoCuenta = useCallback(async () => {
        if (!barrio?.id || !ufSel || !periodo) return;
        setLoading(true);
        setBuscado(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/estadoCuentas/GetEstadoCuentaPeriodo`, {
                params: {
                    administracionId,
                    barrioId: barrio.id,
                    unidadFuncionalId: ufSel,
                    periodo: formatDate(periodo),
                },
            });
            setEstadoCuenta(data ?? []);
        } catch {
            setEstadoCuenta([]);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo obtener el estado de cuenta', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, ufSel, periodo, administracionId]);

    return (
        <>
            <h4>Estado de Cuenta</h4>
            <br />
            <Toast ref={toast} />

            <div className="flex align-items-end gap-3 mb-4 flex-wrap">
                <div>
                    <label style={lbl}>{t.UnidadFuncional} *</label>
                    <Dropdown
                        value={ufSel}
                        options={ufs}
                        optionLabel="unidadFuncional"
                        optionValue="unidadFuncionalId"
                        onChange={(e) => setUfSel(e.value)}
                        placeholder={`Seleccionar ${t.UF}`}
                        filter
                        className="p-inputtext-sm"
                        style={{ width: 220 }}
                    />
                </div>
                <div>
                    <label style={lbl}>Período *</label>
                    <Calendar
                        value={periodo}
                        onChange={(e) => setPeriodo(e.value)}
                        view="month"
                        dateFormat="mm/yy"
                        placeholder="mm/aaaa"
                        className="p-inputtext-sm"
                        style={{ width: 140 }}
                    />
                </div>
                <Button label="Buscar" icon="pi pi-search" className="p-button-sm"
                    onClick={buscarEstadoCuenta} disabled={!ufSel || !periodo} />
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="4" />
                </div>
            ) : buscado && (
                <div className="grid">
                    <div className="col-12">
                        <DataTable value={estadoCuenta} showGridlines responsiveLayout="scroll"
                            emptyMessage={`No hay estado de cuenta registrado para esta ${t.unidadFuncional} en el período seleccionado`}
                            className="p-datatable-gridlines bg-white" style={{ fontSize: '0.88rem' }}>
                            <Column field="periodo" header="Período" sortable body={(r) => formatPeriodo(r.periodo)} style={{ minWidth: 150 }} />
                            <Column field="expensaMes" header="Expensa del mes" sortable body={(r) => formatMoney(r.expensaMes)} style={{ minWidth: 140 }} />
                            <Column field="intereses" header="Intereses" sortable body={(r) => formatMoney(r.intereses)} style={{ minWidth: 120 }} />
                            <Column field="planPago" header="Plan de pago" body={(r) => r.tienePlanPago ? formatMoney(r.planPago) : '—'} style={{ minWidth: 130 }} />
                            <Column field="pagosRealizados" header="Pagos realizados" sortable body={(r) => formatMoney(r.pagosRealizados)} style={{ minWidth: 150 }} />
                            <Column
                                field="saldoFinal"
                                header="Saldo"
                                sortable
                                body={(r) => (
                                    <span style={{ fontWeight: 700, color: (r.saldoFinal ?? 0) <= 0 ? '#12b76a' : '#f04438' }}>
                                        {formatMoney(r.saldoFinal)}
                                    </span>
                                )}
                                style={{ minWidth: 130 }}
                            />
                            <Column field="fechaVencimiento" header="Vencimiento" body={(r) => r.fechaVencimiento ? new Date(r.fechaVencimiento).toLocaleDateString('es-AR') : '—'} style={{ minWidth: 120 }} />
                            <Column header="Estado" body={estadoBadge} style={{ minWidth: 110 }} />
                        </DataTable>
                    </div>
                </div>
            )}
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default EstadoCuentaAdmin;
